import { useState, useEffect } from "react"
import { Alert, Button, Flex, Input, Modal, Select, TimePicker } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Text } from "ui/Typography"
import styles from "./AddAvailabilityScheduleModal.module.css"

const DAYS = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
]

const INTERVAL_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
]

const generateTimes = (from, to, intervalMinutes) => {
  if (!from || !to || !intervalMinutes) return []
  const times = []
  let current = from.clone()
  while (current.isBefore(to) || current.isSame(to, "minute")) {
    times.push(current.format("hh:mm A"))
    current = current.add(intervalMinutes, "minute")
  }
  return times
}

export const AddAvailabilityScheduleModal = ({ open, onClose, onSave, existingSchedules = [], editingSchedule = null }) => {
  const { t } = useTranslation()
  const isEditing = !!editingSchedule

  const [name, setName] = useState("")
  const [selectedDays, setSelectedDays] = useState([])
  const [fromTime, setFromTime] = useState(null)
  const [toTime, setToTime] = useState(null)
  const [interval, setInterval] = useState(null)
  // previewTimes: string[] — full list of generated times
  // disabledTimes: Set<string> — user-toggled off times
  const [previewTimes, setPreviewTimes] = useState([])
  const [disabledTimes, setDisabledTimes] = useState(new Set())
  const [conflictDays, setConflictDays] = useState([])

  useEffect(() => {
    if (!open) return
    if (editingSchedule) {
      setName(editingSchedule.name || "")
      setSelectedDays(editingSchedule.days || [])
      setFromTime(editingSchedule.from_time ? dayjs(editingSchedule.from_time, "HH:mm") : null)
      setToTime(editingSchedule.to_time ? dayjs(editingSchedule.to_time, "HH:mm") : null)
      setInterval(editingSchedule.interval || null)
      setPreviewTimes(editingSchedule.start_times || [])
      setDisabledTimes(new Set())
    } else {
      setName("")
      setSelectedDays([])
      setFromTime(null)
      setToTime(null)
      setInterval(null)
      setPreviewTimes([])
      setDisabledTimes(new Set())
    }
    setConflictDays([])
  }, [open, editingSchedule])

  // Auto-generate preview whenever from/to/interval change
  useEffect(() => {
    if (!fromTime || !toTime || !interval) {
      setPreviewTimes([])
      setDisabledTimes(new Set())
      return
    }
    const generated = generateTimes(fromTime, toTime, interval)
    setPreviewTimes(generated)
    setDisabledTimes(new Set())
  }, [fromTime, toTime, interval])

  const takenDays = new Set(
    existingSchedules
      .filter((s) => !editingSchedule || s.id !== editingSchedule.id)
      .flatMap((s) => s.days || [])
  )

  const handleDayToggle = (dayKey) => {
    const isConflict = takenDays.has(dayKey)
    if (isConflict) {
      setConflictDays((prev) => (prev.includes(dayKey) ? prev : [...prev, dayKey]))
      return
    }
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
    )
    setConflictDays((prev) => prev.filter((d) => d !== dayKey))
  }

  const handleToggleTime = (time) => {
    setDisabledTimes((prev) => {
      const next = new Set(prev)
      if (next.has(time)) next.delete(time)
      else next.add(time)
      return next
    })
  }

  const timeError = !!(fromTime && toTime && !toTime.isAfter(fromTime))
  const activeTimes = previewTimes.filter((t) => !disabledTimes.has(t))
  const selectedDayLabels = selectedDays.map((d) => DAYS.find((x) => x.key === d)?.label).filter(Boolean).join(", ")

  const canSave = name.trim().length > 0 && activeTimes.length > 0 && !timeError

  const handleSave = () => {
    onSave({
      id: editingSchedule?.id ?? Date.now(),
      name: name.trim(),
      days: selectedDays,
      from_time: fromTime?.format("HH:mm") ?? null,
      to_time: toTime?.format("HH:mm") ?? null,
      interval,
      start_times: activeTimes,
    })
    onClose()
  }

  const title = isEditing
    ? t("listings.tabs.scheduling.sections.availability_schedule.modal.edit_title")
    : t("listings.tabs.scheduling.sections.availability_schedule.modal.add_title")

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Flex gap={24} className={styles.body} align="flex-start">
        {/* Left — Set Start Times */}
        <div className={styles.leftPanel}>
          <Text fw={600} size="lg" className="block mb-l">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.add_title")}
          </Text>

          {/* Name */}
          <div className="mb-l">
            <Text className="block mb-xs">
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.name")}
              <span className={styles.required}>*</span>
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekends"
            />
          </div>

          {/* Day selector */}
          <Text className="block mb-s">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.select_days")}
          </Text>
          <div className={styles.dayRow}>
            {DAYS.map(({ key, label }) => {
              const isSelected = selectedDays.includes(key)
              const isConflict = conflictDays.includes(key)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleDayToggle(key)}
                  className={`${styles.dayButton} ${isSelected ? styles.dayButtonSelected : ""} ${isConflict ? styles.dayButtonConflict : ""}`}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {conflictDays.length > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-l mt-s"
              message={t("listings.tabs.scheduling.sections.availability_schedule.modal.day_conflict")}
            />
          )}

          {/* From / To */}
          <Flex gap={16} className="mb-l mt-l">
            <div className={styles.timeField}>
              <Text className="block mb-xs">
                {t("listings.tabs.scheduling.sections.availability_schedule.modal.from")}
                <span className={styles.required}>*</span>
              </Text>
              <TimePicker
                use12Hours
                format="hh:mm A"
                value={fromTime}
                onChange={setFromTime}
                className={styles.timePicker}
              />
            </div>
            <div className={styles.timeField}>
              <Text className="block mb-xs">
                {t("listings.tabs.scheduling.sections.availability_schedule.modal.to")}
                <span className={styles.required}>*</span>
              </Text>
              <TimePicker
                use12Hours
                format="hh:mm A"
                value={toTime}
                onChange={setToTime}
                className={styles.timePicker}
                status={timeError ? "error" : ""}
              />
              {timeError && (
                <Text type="danger" size="sm" className="block mt-xxs">
                  {t("listings.tabs.scheduling.sections.availability_schedule.modal.to_after_from")}
                </Text>
              )}
            </div>
          </Flex>

          {/* Interval */}
          <div>
            <Text className="block mb-xs">
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.interval")}
            </Text>
            <Select
              options={INTERVAL_OPTIONS}
              value={interval}
              onChange={setInterval}
              placeholder="Select"
              className={styles.intervalSelect}
            />
          </div>
        </div>

        {/* Right — Preview */}
        <div className={styles.rightPanel}>
          <Text fw={600} size="lg" className="block mb-m">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.preview_title")}
          </Text>

          {previewTimes.length === 0 ? (
            <Text type="secondary" className={styles.noPreview}>
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.no_preview")}
            </Text>
          ) : (
            <>
              <div className={styles.previewInfo}>
                <InfoCircleOutlined className={styles.previewInfoIcon} />
                <Text size="sm">
                  {t("listings.tabs.scheduling.sections.availability_schedule.modal.preview_info")}
                </Text>
              </div>
              {selectedDayLabels && (
                <Text fw={700} className="block mb-s mt-m">
                  {selectedDayLabels}
                </Text>
              )}
              <Flex gap={6} wrap="wrap">
                {previewTimes.map((time) => {
                  const isDisabled = disabledTimes.has(time)
                  return (
                    <button
                      key={time}
                      type="button"
                      onClick={() => handleToggleTime(time)}
                      className={`${styles.timeChip} ${isDisabled ? styles.timeChipDisabled : styles.timeChipActive}`}
                    >
                      {time}
                    </button>
                  )
                })}
              </Flex>
            </>
          )}
        </div>
      </Flex>

      {/* Footer */}
      <Flex gap={12} className={styles.footer}>
        <Button shape="round" onClick={onClose}>{"Cancel"}</Button>
        <Button type="primary" shape="round" disabled={!canSave} onClick={handleSave}>
          {isEditing
            ? t("listings.tabs.scheduling.sections.availability_schedule.modal.update")
            : t("listings.tabs.scheduling.sections.availability_schedule.modal.save")}
        </Button>
      </Flex>
    </Modal>
  )
}

AddAvailabilityScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  existingSchedules: PropTypes.array,
  editingSchedule: PropTypes.object,
}
